import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Dropdown, Dropdownapi } from '../Utils/tools';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { AgGridReact } from 'ag-grid-react';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AG_GRID_LOCALE_DE } from '@ag-grid-community/locale';
import { orderProductService } from '../../services/orderProductService';
import { filterService } from '../../services/filterService';

function Orderproductlist({ providerId }) {
    const [lastname, setLastname] = useState('');
    const [lastnameInput, setLastnameInput] = useState('');
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [selectedProductOfferTypeId, setSelectedProductOfferTypeId] = useState(null);
    const [selectedCustomerTypeId, setSelectedCustomerTypeId] = useState(null);
    const [selectedShippingAddressCountryId, setSelectedShippingAddressCountryId] = useState(null);
    const [selectedOrderProductStatusId, setSelectedOrderProductStatusId] = useState(null);
    const [selectedSendAdvertisingMail, setSelectedSendAdvertisingMail] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const reloadTimer = useRef(null);

    const localeText = AG_GRID_LOCALE_DE;

    const lKZ = useMemo(() => [
        { value: '1', label: 'Deutschland DE' },
        { value: '2', label: 'Schweiz CH' },
        { value: '3', label: 'Österreich AT' },
        { value: '', label: 'Alle' },
    ], []);

    const magazin = useMemo(() => [
        { value: 'true', label: 'Opt_in' },
        { value: 'false', label: 'Opt_out' },
        { value: '', label: 'Alle' },
    ], []);

    const colDefs = useMemo(() => [
        { field: "ID", filter: true, width: 100 },
        { field: "Datum", filter: true, width: 120 },
        { field: "Zeitschrift", filter: true, width: 150 },
        { field: "Angebotsart", filter: true, width: 130 },
        { field: "Logistikpartner", filter: true, width: 140 },
        { field: "Lieferbeginn", filter: true, width: 120 },
        { field: "Folge-RG", filter: true, width: 100 },
        { field: "Soll", filter: true, width: 120 },
        { field: "Ist", filter: true, width: 120 },
        { field: "Lieferende", filter: true, width: 120 },
        { field: "Kunde", filter: true, width: 100 },
        { field: "K-E-Mail", filter: true, width: 200 },
        { field: "Abopreis", filter: true, width: 100 },
        { field: "Währung-Code", filter: true, width: 120 },
        { field: "Währung-Symbol", filter: true, width: 130 },
        { field: "Zahlungsart", filter: true, width: 130 },
        { field: "Zahlungsrhythmus", filter: true, width: 150 },
        { field: "Abodauer", filter: true, width: 100 },
        { field: "L-Anrede", filter: true, width: 100 },
        { field: "L-Vorname", filter: true, width: 120 },
        { field: "L-Nachname", filter: true, width: 120 },
        { field: "L-Firma", filter: true, width: 150 },
        { field: "L-Strasse", filter: true, width: 150 },
        { field: "L-PLZ", filter: true, width: 100 },
        { field: "L-Ort", filter: true, width: 130 },
        { field: "L-LKZ", filter: true, width: 100 },
        { field: "L=R", filter: true, width: 80 },
        { field: "Newsletter", filter: true, width: 100 }
    ], []);

    useEffect(() => {
        const THREE_HOURS_MS = 3 * 60 * 60 * 1000; // 3 Stunden in Millisekunden

        reloadTimer.current = setTimeout(() => {
            window.location.reload();
        }, THREE_HOURS_MS);

        return () => {
            if (reloadTimer.current) {
                clearTimeout(reloadTimer.current);
            }
        };
    }, []);

    const handleSelect = useCallback((value, key) => {
        setPageNumber(1);

        switch (key) {
            case 'productId':
                setSelectedProductId(value);
                break;
            case 'productOfferTypeId':
                setSelectedProductOfferTypeId(value);
                break;
            case 'customerTypeId':
                setSelectedCustomerTypeId(value);
                break;
            case 'shippingAddressCountryId':
                setSelectedShippingAddressCountryId(value);
                break;
            case 'sendAdvertisingMail':
                setSelectedSendAdvertisingMail(value);
                break;
            default:
                break;
        }
    }, []);

    const handleSearchClick = useCallback(() => {
        setLastname(lastnameInput);
        setPageNumber(1);
    }, [lastnameInput]);

    const handleClearSearch = useCallback(() => {
        setLastnameInput('');
        setLastname('');
        setPageNumber(1);
    }, []);

    useEffect(() => {
        if (!providerId) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const filters = {
                    productId: selectedProductId?.value,
                    customerTypeId: selectedCustomerTypeId?.value,
                    productOfferTypeId: selectedProductOfferTypeId?.value,
                    shippingAddressCountryId: selectedShippingAddressCountryId?.value,
                    orderProductStatusId: selectedOrderProductStatusId?.value,
                    sendAdvertisingMail: selectedSendAdvertisingMail?.value,
                    shippingAddressLastname: lastname,
                    providerId: providerId,
                    pageNumber: pageNumber
                };

                const data = await orderProductService.getOrderProductList(filters);
                const mappedData = orderProductService.mapToGridData(data);

                setList(mappedData);
            } catch (error) {
                setError('Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.');
                setList([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [
        providerId,
        selectedProductId,
        selectedCustomerTypeId,
        selectedProductOfferTypeId,
        selectedShippingAddressCountryId,
        selectedOrderProductStatusId,
        selectedSendAdvertisingMail,
        lastname,
        pageNumber
    ]);

    const incrementPageNumber = useCallback(() => {
        setPageNumber(prevPageNumber => prevPageNumber + 1);
    }, []);

    const decrementPageNumber = useCallback(() => {
        setPageNumber(prevPageNumber => Math.max(1, prevPageNumber - 1));
    }, []);

    return (
        <div className="admin_container">
            <Container fluid className="dropdowncontainer">
                <Row>
                    <Col xs={12} sm={6} md={4} lg={3} xl={2}>
                        <h4>Zeitschrift</h4>
                        <Dropdownapi
                            apiUrl={filterService.getProductListUrl(providerId)}
                            labelKey='productName'
                            valueKey='productId'
                            onSelectedItemChange={handleSelect}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3} xl={2}>
                        <h4>Kundentyp</h4>
                        <Dropdownapi
                            apiUrl={filterService.getCustomerTypeUrl()}
                            labelKey='type'
                            valueKey='customerTypeId'
                            onSelectedItemChange={handleSelect}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3} xl={2}>
                        <h4>L-LKZ</h4>
                        <Dropdown
                            options={lKZ}
                            onSelectedItemChange={handleSelect}
                            valueKey='shippingAddressCountryId'
                        />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3} xl={2}>
                        <h4>Newsletter</h4>
                        <Dropdown
                            options={magazin}
                            onSelectedItemChange={handleSelect}
                            valueKey='sendAdvertisingMail'
                        />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3} xl={3}>
                        <h4>L-Nachname</h4>
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control"
                                value={lastnameInput}
                                onChange={(e) => setLastnameInput(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearchClick();
                                    }
                                }}
                                placeholder="Nachname suchen..."
                                aria-label="Nachname suchen"
                            />
                            {lastnameInput && (
                                <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={handleClearSearch}
                                    title="Zurücksetzen"
                                >
                                    ×
                                </button>
                            )}
                            <button
                                className="btn btn-primary"
                                type="button"
                                onClick={handleSearchClick}
                                disabled={loading}
                                title="Suchen"
                            >
                            </button>
                        </div>
                    </Col>
                </Row>
            </Container>

            {error && (
                <div className="alert alert-danger mt-3" role="alert">
                    {error}
                </div>
            )}

            <div className="ag-theme-quartz mt-3" style={{ height: 700 }}>
                <AgGridReact
                    rowData={list}
                    columnDefs={colDefs}
                    localeText={localeText}
                    loading={loading}
                    overlayNoRowsTemplate="<span>Keine Daten vorhanden</span>"
                />
            </div>

            <div className="paging mt-3">
                <button
                    className="page-btn"
                    onClick={decrementPageNumber}
                    disabled={pageNumber === 1 || loading}
                    aria-label="Vorherige Seite"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <div>Seite: {pageNumber}</div>
                <button
                    className="page-btn"
                    onClick={incrementPageNumber}
                    disabled={loading}
                    aria-label="Nächste Seite"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
        </div>
    );
}

export default Orderproductlist;